import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography
} from "@mui/material";
import {
    NotificationChannel,
    NotificationRecipientIdType,
    NotificationResourceType,
    NotificationType,
    Property
} from "../../API";
import {
    useMemo,
    useState
} from "react";

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { IssueSubmissionNotificationSettingForm } from "./IssueSubmissionNotificationSettingForm";
import _ from "lodash";
import { useContacts } from "./contexts/ContactContextProvider";
import { useCurrentUser } from "./contexts/CurrentUserContext";
import { useResourceNotificationSettings } from "./contexts/ResourceNotificationSettingContextProvider";

type PropertySettingProps = {
    property: Property;
};

export const PropertySetting = (props: PropertySettingProps) => {
    const { property } = props;
    const [openIssueSubmissionNotificationSettingForm, setOpenIssueSubmissionNotificationSettingForm] = useState(false);
    const { contacts } = useContacts();
    const { userId } = useCurrentUser();

    const {
        issueSubmissionNotificationSetting: existingIssueSubmissionNotificationSettings,
        updateIssueSubmissionNotificationSetting
    } = useResourceNotificationSettings();

    const issueSubmissionNotificationSettings = useMemo(() => {
        if (existingIssueSubmissionNotificationSettings) {
            return existingIssueSubmissionNotificationSettings;
        }
        // Default settings
        return {
            resourceType: NotificationResourceType.PROPERTY,
            resourceId: property.id,
            notificationType: NotificationType.EXTERNAL_SUBMISSION,
            recipients: [{
                recipientId: property.createdBy,
                recipientIdType: NotificationRecipientIdType.USER,
                enabled: true,
                notificationChannels: [NotificationChannel.EMAIL]
            }],
            enabled: true
        };
    }, [existingIssueSubmissionNotificationSettings, property]);

    return (
        <>
            <Box display="flex" flexDirection="column" mt={1}>
                <Typography fontSize="1.5rem" fontWeight={500}>
                    Settings
                </Typography>
                <Box display="flex" flexDirection="column" ml={1}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography fontSize="1.125rem" fontWeight={500}>
                            Issue Submission Notification
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            {issueSubmissionNotificationSettings.enabled ? (
                                <>
                                    <CheckCircleIcon color="success" />
                                    <Typography>
                                        Enabled
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <CancelIcon color="error" />
                                    <Typography>
                                        Disabled
                                    </Typography>
                                </>
                            )}
                        </Box>
                        {updateIssueSubmissionNotificationSetting && (
                            <Button
                                variant="outlined"
                                onClick={() => setOpenIssueSubmissionNotificationSettingForm(true)}
                                sx={{ textTransform: "none" }}
                            >
                                Update
                            </Button>
                        )}
                    </Box>
                    {!existingIssueSubmissionNotificationSettings && (
                        <Typography variant="body2" color="textSecondary">
                            ( Default settings are applied )
                        </Typography>
                    )}
                    {issueSubmissionNotificationSettings.enabled && (
                        <>
                            <Typography fontWeight={500} mt={1}>
                                Recipients:
                            </Typography>
                            {!issueSubmissionNotificationSettings.recipients || issueSubmissionNotificationSettings.recipients.length === 0 ? (
                                <Typography>
                                    Please configure recipients for this notification
                                </Typography>
                            ) : (
                                issueSubmissionNotificationSettings.recipients.map((recipient, index) => {
                                    let recipientName: string | undefined;
                                    if (recipient.recipientIdType === NotificationRecipientIdType.USER) {
                                        if (recipient.recipientId === userId) {
                                            recipientName = "You";
                                        } else {
                                            recipientName = "Unknown User";
                                        }
                                    } else if (recipient.recipientIdType === NotificationRecipientIdType.CONTACT) {
                                        const contact = contacts?.find(contact => contact.id === recipient.recipientId);
                                        recipientName = contact?.name ?? undefined;
                                    }
                                    if (!recipientName) {
                                        return null;
                                    }
                                    return (
                                        <Box ml={1} key={index}>
                                            <Typography>
                                                {index + 1}. {recipientName}, Notification type: {recipient.notificationChannels ? recipient.notificationChannels.map(c => _.capitalize(c)).join(', ') : 'N/A'}
                                            </Typography>
                                        </Box>
                                    );
                                })
                            )}
                        </>
                    )}
                </Box>
            </Box>
            {updateIssueSubmissionNotificationSetting && (
                <Dialog
                    open={openIssueSubmissionNotificationSettingForm}
                    onClose={() => setOpenIssueSubmissionNotificationSettingForm(false)}
                >
                    <DialogTitle>
                        Issue Submission Notification Setting
                    </DialogTitle>
                    <DialogContent sx={{ width: "xs" }}>
                        <DialogContentText sx={{ mb: 2 }}>
                            Configure who should receive notifications when an issue is submitted for this property.
                        </DialogContentText>
                        <IssueSubmissionNotificationSettingForm
                            existingIssueSubmissionNotificationSettings={issueSubmissionNotificationSettings}
                            onCancel={() => setOpenIssueSubmissionNotificationSettingForm(false)}
                            onSave={async (newIssueSubmissionNotificationSettings) => {
                                await updateIssueSubmissionNotificationSetting(newIssueSubmissionNotificationSettings);
                                setOpenIssueSubmissionNotificationSettingForm(false);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};